import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, Clapperboard, Loader2, Facebook, Youtube, Instagram, Globe, Music2, Settings2 } from "lucide-react";
import { api, apiError, mediaUrl } from "@/lib/api";

const ICONS = { facebook: Facebook, tiktok: Music2, youtube: Youtube, instagram: Instagram, website: Globe };
const LABELS = { facebook: "Facebook", tiktok: "TikTok", youtube: "YouTube", instagram: "Instagram", website: "Site / blog" };

const ago = (iso) => {
  if (!iso) return "";
  const h = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 3600000));
  return h < 24 ? `il y a ${h} h` : `il y a ${Math.round(h / 24)} j`;
};

function BloggerCard({ b, onCreated }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    const t = toast.loading(b.latest ? "Lancement de la vidéo…" : "Récupération de son dernier contenu… (jusqu'à 1 min)");
    try {
      await api.bloggerVideo(b.index);
      toast.success(`Vidéo de ${b.name} lancée — prête dans ~1 min dans la galerie`, { id: t });
      onCreated?.();
    } catch (e) {
      toast.error(apiError(e), { id: t });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-xl border border-[#e4e6eb] bg-white p-4 flex flex-col gap-3 hover:shadow-md transition-shadow" data-testid={`blogger-card-${b.index}`}>
      <div className="flex items-center gap-2 min-w-0">
        <span className="w-9 h-9 rounded-full bg-[#e7f3ff] text-[#1877f2] font-bold grid place-items-center shrink-0">{(b.name || "?").charAt(0).toUpperCase()}</span>
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate" data-testid={`blogger-card-name-${b.index}`}>{b.name}</div>
          <div className="flex gap-1.5 mt-0.5">
            {Object.keys(b.sources).map(k => { const I = ICONS[k] || Globe; return <I key={k} className="w-3.5 h-3.5 text-[#65676b]" title={LABELS[k]} aria-label={LABELS[k]} />; })}
          </div>
        </div>
      </div>
      {b.latest ? (
        <a href={b.latest.source_url || "#"} target="_blank" rel="noreferrer" className="flex gap-2 group" data-testid={`blogger-latest-${b.index}`}>
          {b.latest.thumbnail && <img src={mediaUrl(b.latest.thumbnail)} alt="" onError={e => { e.currentTarget.style.display = "none"; }} className="w-14 h-14 rounded-lg object-cover shrink-0" />}
          <div className="min-w-0">
            <div className="text-xs font-semibold line-clamp-2 group-hover:text-[#1877f2]">{b.latest.title}</div>
            <div className="text-[11px] text-[#8a8d91] mt-0.5">{b.latest.source} · {ago(b.latest.posted_at || b.latest.created_at)}</div>
          </div>
        </a>
      ) : (
        <p className="text-xs text-[#8a8d91]" data-testid={`blogger-no-content-${b.index}`}>Pas encore de contenu récupéré — le bouton ira chercher son dernier contenu.</p>
      )}
      <button type="button" onClick={go} disabled={busy} className="btn-primary justify-center mt-auto !py-2 text-sm" data-testid={`blogger-video-btn-${b.index}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clapperboard className="w-4 h-4" />} Générer une vidéo
      </button>
    </div>
  );
}

export const BloggersPanel = ({ onCreated, refreshKey }) => {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.bloggers().then(setData).catch(() => {}), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!data) return null;
  const list = data.bloggers.filter(b => Object.keys(b.sources).length);
  return (
    <section className="card-surface p-5" data-testid="bloggers-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><Users className="w-5 h-5 text-[#1877f2]" /> Mes Blogueurs
          <span className="text-sm font-semibold text-[#65676b]" data-testid="bloggers-panel-count">{data.max ? `${list.length}/${data.max}` : `${list.length} sources`}</span></h2>
        <Link to="/app/agent" className="btn-ghost !py-1.5 text-sm" data-testid="bloggers-manage-link"><Settings2 className="w-4 h-4" /> Gérer mes sources</Link>
      </div>
      {list.length === 0 ? (
        <p className="text-sm text-[#65676b]" data-testid="bloggers-empty">Ajoutez jusqu'à {data.max || 15} sources (Facebook, TikTok, YouTube, Instagram, blog ou site) dans « Gérer mes sources ».</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {list.map(b => <BloggerCard key={b.index} b={b} onCreated={() => { onCreated?.(); load(); }} />)}
        </div>
      )}
      <p className="text-[11px] text-[#8a8d91] mt-3">Chaque vidéo compte dans votre quota mensuel. Blogs, sites et YouTube sont lus gratuitement ; Instagram et TikTok via notre service de secours.</p>
    </section>
  );
};
